import React from "react";
import {
  Table,
  Drawer,
  Row,
  Col,
  Typography,
  Divider,
  Tag,
} from "antd";
import { Product, ProductVariant } from "../../../../types/product";
import { productTypes } from "../../../../utils/constants";
import TitleSubtitle from "../../../../components/titleSubtitle";
import ProductList from "../../../../components/productsList";

const { Title, Text } = Typography;

interface DetailDrawerProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  product?: Product | null;
  setProduct: (product: Product | null) => void;
}

const DetailDrawer: React.FC<DetailDrawerProps> = ({
  open,
  setOpen,
  product,
  setProduct,
}) => {

  const onClose = (): void => {
    setOpen(false);
    setProduct(null);
  };

  const columns = [
    {
      title: "Nombre",
      key: "name",
      render: (row) => <Text strong>{row.product.name}</Text>,
    },
    {
      title: "Máximo",
      key: "max",
      render: (row) => <Text>{row.max}</Text>,
    },
    {
      title: "Mínimo",
      key: "min",
      render: (row) => <Text>{row.min}</Text>,
    },
    {
      title: 'Variantes',
      key: 'variants',
      render: (row) => {
        const lineItems = row?.product?.variants?.map((p) => ({
          product: {
            _id: p.id,
            img: p.img,
            title: p.title,
            description: p.description,
            product: {
              name: p.product.name,
              description: p.product.description,
            },
          },
        })) ?? [];

        if(lineItems.length > 0){
          return <ProductList lineItems={lineItems} />;
        } else {
          return <Tag color="yellow">Sin Productos</Tag>
        }
      },
    },
  ];

  // Agrupar variantes por descripción
  const groupedVariants = product?.variants.reduce((acc, variant) => {
    if (variant?.description) {
      if (!acc[variant?.description]) {
        acc[variant?.description] = [];
      }
      acc[variant?.description].push(variant);
    }
    return acc;
  }, {} as Record<string, ProductVariant[]>) || {};

  return (
    <Drawer
      title="Detalles del producto"
      placement="right"
      width="50%"
      onClose={onClose}
      visible={open}
    >
      <Row gutter={10} justify="center">
        <Col span={6}>
          <img
            src={product?.variants[0]?.img}
            alt="Product"
            style={{ width: 100,
              height: 100,
              borderRadius: 50 }}
          />
        </Col>
        <Col span={18}>
          <Title level={3}>{product?.name}</Title>
          <Typography.Paragraph>{product?.description}</Typography.Paragraph>
          <Tag color="blue">{product ? productTypes[product.type] : "Desconocido"}</Tag>
        </Col>
      </Row>
      <Divider />

      <Title level={3}>Variantes</Title>
      {product && product.variants.length <= 1 ? (
        <Title level={4}>Precio: $ {product?.variants[0]?.price}</Title>
      ) : (
        Object.entries(groupedVariants || {}).map(([description, variants]) => (
          <div key={description}>
            <Title level={4}>{description}</Title>
            <Table
              size="small"
              dataSource={variants}
              columns={[
                {
                  dataIndex: "img",
                  key: "img",
                  render: (img: string) => {
                    return (
                      <img
                        src={img}
                        style={{ width: 80,
                          height: 80,
                          borderRadius: 50 }}
                      />
                    );
                  },
                },
                {
                  title: "Título",
                  dataIndex: "title",
                  key: "title",
                  render: (text: string) => <Title level={5}>{text}</Title>,
                },
                {
                  title: "Precio",
                  dataIndex: "price",
                  key: "price",
                  render: (text: string) => <pre>$ {text}</pre>,
                },
              ]}
              rowKey="id"
              pagination={false}
            />
          </div>
        ))
      )}
      <Divider />
      <Row>
        <Col span={12}>
          {product?.rulesAdditionals?.maxComplements && (
            <>
              <TitleSubtitle
                title="Maximo de Complementos"
                subtitle={String(product?.rulesAdditionals?.maxComplements)}
              />
            </>
          )}
        </Col>
        <Col span={12}>
          {product?.rulesAdditionals?.maxBeverages && (
            <TitleSubtitle
              title="Maximo de Bebidas"
              subtitle={String(product?.rulesAdditionals?.maxBeverages)}
            />
          )}
        </Col>
      </Row>
      {product?.rulesAdditionals?.includesExtras && (
        <>
          <Divider />
          <Title level={3}>Extras</Title>
          <div style={{ marginTop: 10 }}>
            <Table
              dataSource={product?.rulesAdditionals?.extraRules ?? []}
              rowKey="id"
              columns={columns}
            />
          </div>
        </>
      )}
    </Drawer>
  );
};

export default DetailDrawer;
